import { Alpine } from '../core/alpine-core.js';
import PineconeRouter from 'pinecone-router';
import { attachPineconeLegacyUiEvents } from '../core/pinecone-legacy-events.js';
import {
  ADMIN_LOGIN_ABS,
  absoluteAdminPathnameFromPineconePath,
  isAdminShellPublicPath,
  isAuthExemptAdminShellUrl,
  sanitizePostLoginNext,
} from './router-guards.js';

function pineconePathIsPublicAuth(path) {
  return isAdminShellPublicPath(absoluteAdminPathnameFromPineconePath(path));
}

if (!window.__PINECONE_INSTALLED__) {
  Alpine.plugin(PineconeRouter);
  window.__PINECONE_INSTALLED__ = true;
}

// Get instance exposed by the plugin
const Router = /** @type {any} */ (window).PineconeRouter;

// Always apply admin settings. Pinecone's `addBasePath` treats `basePath` `''` or `/` as
// "already prefixed" for any absolute path, so routes would register as `/account-settings`
// while the real URL is `/admin/account-settings` → ROUTE_NOT_FOUND. A prior bundle that
// set `Router.__configured` (public shell) must not skip this block in exotic/HMR cases.
Router.settings({
  basePath: '/admin',
  targetID: 'admin',
  handleClicks: true,       // let Pinecone intercept <a> clicks
  scrollTopOnNav: true
});

attachPineconeLegacyUiEvents();

// After each navigation: scroll only. Pinecone-router already calls `Alpine.initTree` on each
// injected route fragment (`pinecone-router` `$()` → `o.forEach(s => e.initTree(s))`).
// Calling `initTree` again on `#admin` duplicates directives (`x-for` tabs, dashboard overlays, etc.).
// Same pattern as `src/js/public/router.js` (`__PINECONE_AFTER__` guard, no re-init).
document.addEventListener('pinecone:after', () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
});

document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a');
  if (!a) return;

  const href = a.getAttribute('href') || '';
  const isExternalMarked = a.matches('[rel="external"],[data-external]');

  // If it's marked external OR it's not an /admin route, let the browser handle it.
  if (isExternalMarked || (href.startsWith('/') && !href.startsWith('/admin'))) {
    // Stop Pinecone’s internal click handler from seeing this event,
    // but DO NOT preventDefault so the browser navigates.
    e.stopImmediatePropagation();
    return;
  }
}, true); // <-- capture phase

// Simple matcher for patterns like '/teams/:id/edit'
function matchPath(pattern, path) {
  const names = [];
  const rx = new RegExp('^' + pattern
    .replace(/\//g, '\\/')
    .replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, n) => { names.push(n); return '([^/]+)'; }) + '$');
  const m = path.match(rx);
  if (!m) return { ok: false, params: {} };
  const params = {};
  names.forEach((n, i) => params[n] = m[i + 1]);
  return { ok: true, params };
}

function rememberFrom() {
  const from = sanitizePostLoginNext(location.pathname + location.search + location.hash);
  sessionStorage.setItem('NOAUTH_FROM', from);
  return from;
}

function redirectToLogin(from) {
  const url = new URL(ADMIN_LOGIN_ABS, location.origin);
  url.searchParams.set('next', sanitizePostLoginNext(from));
  window.location.href = url.toString();
}

/** Pinecone may emit `/login` (relative) or `/admin/login` (absolute) depending on version / navigation source. */
function pineconePathIsLogin(path) {
  const p = String(path || '').split('?')[0].replace(/\/+$/, '') || '/';
  return p === '/login' || p === '/admin/login';
}

function pineconePathIsNoAuth(path) {
  const p = String(path || '').split('?')[0].replace(/\/+$/, '') || '/';
  return p === '/no-auth' || p === '/admin/no-auth';
}

function redirectNoAuth() {
  const from = rememberFrom();
  Router.navigate('/admin/no-auth?from=' + encodeURIComponent(from), { replace: true });
}


document.addEventListener('pinecone:before', (ev) => {
  const path = ev?.detail?.path || '/';
  const S = Alpine.store('session');

  // If you want *all* /admin to require login:
  if (!S?.isAuthed && !pineconePathIsPublicAuth(path) && !isAuthExemptAdminShellUrl()) {
    // send to login, keep original in NOAUTH_FROM so "Go back" can work too
    const from = rememberFrom();
    redirectToLogin(from);
    return;
  }

  // Hide chrome on login / no-auth / kiosk JWT routes (same rules as `index.js` — Pinecone path is under `/admin`)
  Alpine.store('ui')?.setHideChrome?.(
    isAdminShellPublicPath(absoluteAdminPathnameFromPineconePath(path)),
  );
  document.getElementById('admin')?.classList.remove('layout-scorer-bleed');

  // ---- Guard rules (add as many as you need) ----

  // Admin-only area example
  // if (path.startsWith('/settings')) {
  //   const ok = S.hasRole('admin');
  //   if (!ok) return redirectNoAuth();
  // }

  // Team edit: require Admin OR (captain of that team)
  {
    const { ok, params } = matchPath('/admin/teams/:id/edit', path);
    if (ok) {
      const teamId = params.id;
      // Client-side check: admin OR (I’m captain of this team).
      // NOTE: this uses your session store helper that matches registeredTeamId + captainPlayerId logic
      const can =
        S.hasRole('admin') ||
        S.isCaptainOfTeam(teamId, /* captainId is baked into the helper from template or token; see below */ S.me?.teamCaptainId);
      if (!can) return redirectNoAuth();
    }
  }

  // Player self edit example: admin or self
  {
    const { ok, params } = matchPath('/admin/players/:id/edit', path);
    if (ok) {
      const isAdmin = S.hasRole('admin');
      const isSelf = String(S.me?._id || '') === String(params.id || '');
      if (!isAdmin && !isSelf) return redirectNoAuth();
    }
  }

  // Add more guards as needed...
});

export { Router };
